import { Assumption } from '@portal/pkg-api/gen/navigator'
import { forwardRef, SyntheticEvent, useState } from 'react'
import {
  Autocomplete,
  AutocompleteProps,
  Collapse,
  Stack,
  TextField,
  TextFieldProps,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'
import { ParseKeys } from 'i18next'
import { useSet } from '@portal/pkg-hooks'
import ExpandLess from '@mui/icons-material/ExpandLess'
import ExpandMore from '@mui/icons-material/ExpandMore'
import { useFilteredData } from './helper/useFilteredData'

export interface CategorizedAssumptionPickerProps extends Omit<
  AutocompleteProps<Assumption, false, false, false>,
  'value' | 'onChange' | 'renderInput' | 'options'
> {
  value: Assumption | null
  onChange: (value: Assumption | null) => void
  label: string
  inputProps?: Partial<TextFieldProps>
  filters?: {
    entityTypeId?: number
    onlyFilterable?: boolean
    excludePseudo?: boolean
    includedAssumptionTypes?: string[]
    excludedAssumptionTypes?: string[]
    predicate?: (assumption: Assumption) => boolean
  }
}

export const CategorizedAssumptionPicker = forwardRef<
  HTMLDivElement,
  CategorizedAssumptionPickerProps
>(({ value, onChange, label, inputProps = {}, filters = {}, ...props }, ref) => {
  const { t } = useTranslation()

  // Data
  const { assumptions, categories } = useFilteredData(filters)

  // State
  const [open, setOpen] = useState(false)
  const [inputValue, setInputValue] = useState('')
  const [
    expandedCategories,
    {
      add: expandCategory,
      delete: collapseCategory,
      clear: clearExpandedCategories,
      replace: replaceExpandedCategories,
    },
  ] = useSet<number>()

  // Handlers
  const handleOpen = () => {
    if (categories.length > 0) {
      if (value) {
        clearExpandedCategories()
        expandCategory(value.categoryId!)
      } else if (expandedCategories.size <= 0) {
        expandCategory(categories[0].id)
      }
    }
    setOpen(true)
  }
  const handleClose = () => {
    setOpen(false)
  }
  const handleCategoryToggle = (categoryId: number) => () => {
    if (expandedCategories.has(categoryId)) {
      collapseCategory(categoryId)
    } else {
      expandCategory(categoryId)
    }
  }
  const handleChange = (_event: SyntheticEvent, newValue: Assumption | null) => {
    onChange(newValue)
  }
  const handleInputChange = (_event: SyntheticEvent, newInputValue: string) => {
    if (newInputValue) {
      replaceExpandedCategories(categories.map((c) => c.id))
    } else if (categories.length > 0) {
      replaceExpandedCategories([categories[0].id])
    }
    setInputValue(newInputValue)
  }

  return (
    <Autocomplete<Assumption>
      ref={ref}
      options={assumptions}
      autoHighlight
      open={open}
      onOpen={handleOpen}
      onClose={handleClose}
      onInputChange={handleInputChange}
      inputValue={inputValue}
      value={value}
      onChange={handleChange}
      groupBy={(assumption) => assumption.categoryId?.toString() ?? 'none'}
      renderInput={(params) => (
        <TextField variant="outlined" label={label} {...params} {...inputProps} />
      )}
      getOptionLabel={(assumption) => t(assumption.label as ParseKeys)}
      renderOption={(params, { label }) => (
        <Typography {...params} variant="body2">
          {t(label as ParseKeys)}
        </Typography>
      )}
      renderGroup={(params) => {
        const category = categories.find((c) => c.id.toString() === params.group)
        if (!category) {
          return null
        }

        const expanded = expandedCategories.has(category.id)

        return (
          <li key={params.key}>
            <Stack
              direction="row"
              spacing={1}
              onClick={handleCategoryToggle(category.id)}
              sx={{
                alignItems: 'center',
              }}
            >
              {expanded ? <ExpandLess /> : <ExpandMore />}
              <Typography sx={{ fontWeight: 500 }}>{t(category.name as ParseKeys)}</Typography>
            </Stack>
            <Collapse in={expanded} timeout={250} unmountOnExit>
              {params.children}
            </Collapse>
          </li>
        )
      }}
      {...props}
    />
  )
})
CategorizedAssumptionPicker.displayName = 'CategorizedAssumptionPicker'
